(() => {
  'use strict';

  const SELECTORS = {
    recommendationCard: '.grid__item',
    productInfo: 'product-info-cart-upsell',
    recommendationsWrapper: '.cart-drawer__recommendations',
    loadingPlaceholder: '.cart-recommendations-loading-placeholder',
    cartItems: 'cart-items'
  };

  const CLASSES = {
    loading: 'is-loading',
    empty: 'is-empty',
    hasRecommendations: 'has-recommendations'
  };

  function safeJSONParse(value, fallback = null) {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  }

  class ComprehensiveCartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.isLoading = false;
    }

    connectedCallback() {
      this.load();
    }

    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }
    }

    get wrapper() {
      return this.closest(SELECTORS.recommendationsWrapper);
    }

    get productId() {
      return String(this.dataset.productId || '').trim();
    }

    get productsToShow() {
      const value = parseInt(this.dataset.productsToShow, 10);
      return Number.isFinite(value) && value > 0 ? value : 3;
    }

    get cartProductIds() {
      const normalized = String(this.dataset.cartProductIds || '[]').replace(/&quot;/g, '"');
      const parsed = safeJSONParse(normalized, []);
      if (!Array.isArray(parsed)) return new Set();

      return new Set(
        parsed
          .map((id) => String(id).trim())
          .filter(Boolean)
      );
    }

    get requestUrl() {
      const baseUrl = String(this.dataset.url || '').trim();
      if (!baseUrl || !this.productId) return '';

      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}product_id=${encodeURIComponent(this.productId)}`;
    }

    get loadingPlaceholder() {
      return this.querySelector(SELECTORS.loadingPlaceholder);
    }

    setState({ loading = false, empty = false, hasRecommendations = false } = {}) {
      const wrapper = this.wrapper;
      if (!wrapper) return;

      wrapper.classList.toggle(CLASSES.loading, loading);
      wrapper.classList.toggle(CLASSES.empty, empty);
      wrapper.classList.toggle(CLASSES.hasRecommendations, hasRecommendations);
    }

    recheckThemeUpsellVisibility() {
      const cartItems = document.querySelector(SELECTORS.cartItems);

      if (
        cartItems &&
        typeof cartItems.toggleCartUpsellOrBundleWidgetVisibility === 'function'
      ) {
        requestAnimationFrame(() => {
          cartItems.toggleCartUpsellOrBundleWidgetVisibility();
        });
      }
    }

    removeLoadingPlaceholder() {
      if (this.loadingPlaceholder) {
        this.loadingPlaceholder.remove();
      }
    }

    extractProductId(cardElement) {
      const productInfoElement = cardElement.querySelector(SELECTORS.productInfo);
      const productId = productInfoElement?.dataset?.productId;
      return productId ? String(productId).trim() : '';
    }

    renderEmpty() {
      this.innerHTML = '<div class="product-recommendations no-markers"></div>';
      this.removeLoadingPlaceholder();
      this.setState({
        loading: false,
        empty: true,
        hasRecommendations: false
      });
      this.recheckThemeUpsellVisibility();
    }

    renderCards(cards) {
      const container = document.createElement('div');
      container.className = 'product-recommendations no-markers';

      cards.forEach((card) => container.appendChild(card));

      this.innerHTML = '';
      this.appendChild(container);

      this.removeLoadingPlaceholder();
      this.setState({
        loading: false,
        empty: cards.length === 0,
        hasRecommendations: cards.length > 0
      });
      this.recheckThemeUpsellVisibility();
    }

    async load() {
      if (this.isLoading) return;

      const url = this.requestUrl;
      if (!url) {
        this.renderEmpty();
        return;
      }

      this.isLoading = true;
      this.setState({
        loading: true,
        empty: false,
        hasRecommendations: true
      });

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(`Failed to load recommendations: ${response.status}`);
        }

        const html = await response.text();
        const wrapper = document.createElement('div');
        wrapper.innerHTML = html;

        const cards = Array.from(wrapper.querySelectorAll(SELECTORS.recommendationCard));
        const cartProductIds = this.cartProductIds;

        const filteredCards = cards
          .filter((card) => {
            const recommendedProductId = this.extractProductId(card);
            return recommendedProductId && !cartProductIds.has(recommendedProductId);
          })
          .slice(0, this.productsToShow)
          .map((card) => card.cloneNode(true));

        if (!filteredCards.length) {
          this.renderEmpty();
          return;
        }

        this.renderCards(filteredCards);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error loading cart recommendations:', error);
          this.renderEmpty();
        }
      } finally {
        this.isLoading = false;
      }
    }
  }

  if (!customElements.get('comprehensive-cart-recommendations')) {
    customElements.define('comprehensive-cart-recommendations', ComprehensiveCartRecommendations);
  }
})();